import { getFragment } from '../../graphql';
import type {
    CashOfferInfoFragment,
    InsuranceOfferInfoFragment,
} from '../../graphql/generated/graphql';
import type { OfferFragment } from '../../types';

/**
 * Extracts the shipping method from a single offer's shipping prices
 *
 * @param offer - A single offer (CashOffer, InsuranceOffer, or NonPriceable variants)
 * @returns The shipping method string, or undefined if not found
 */
export const getShippingMethodFromOffer = (
    offer?: OfferFragment | null
): string | undefined => {
    if (!offer) {
        return undefined;
    }

    if ('shippingPrices' in offer && offer.shippingPrices) {
        const { shippingPrices } = offer;

        if (shippingPrices.length > 0) {
            const firstShippingPrice = getFragment(shippingPrices[0]);
            if (firstShippingPrice?.shippingMethod) {
                return firstShippingPrice.shippingMethod;
            }
        }
    }

    return undefined;
};

/**
 * Extracts the shipping method from the first available offer's shipping prices
 *
 * @param offers - Array of offers (Ex: CashOffer and/or InsuranceOffer from ProviderOffersCard)
 * @returns The shipping method string, or undefined if not found
 */
export const getShippingMethodFromOffers = (
    offers: Array<CashOfferInfoFragment | InsuranceOfferInfoFragment>
): string | undefined => {
    if (!offers || offers.length === 0) {
        return undefined;
    }

    for (const offer of offers) {
        const shippingMethod = getShippingMethodFromOffer(offer);
        if (shippingMethod) {
            return shippingMethod;
        }
    }

    return undefined;
};
